
import { useEffect, useState, useCallback } from "react";
import Navbar from "@/components/Navbar";
import { useDashboardData } from "@/hooks/dashboard";
import LoadingIndicator from "@/components/dashboard/LoadingIndicator";
import DashboardContent from "@/components/dashboard/DashboardContent";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/auth";

const Dashboard = () => {
  const {
    products,
    lowStockProducts,
    dismissedAlerts,
    showMigration,
    isMigrating,
    isLoading,
    handleMigrateData,
    handleDismissAlert,
    handleAddStock,
    handleUsageRecorded,
    refreshData
  } = useDashboardData();
  
  const [loadTimeout, setLoadTimeout] = useState(false);
  const [loadAttempts, setLoadAttempts] = useState(0);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  
  // Check authentication status
  useEffect(() => {
    if (!authLoading && !user) {
      console.log("No authenticated user, redirecting to auth page");
      navigate("/auth");
    }
  }, [authLoading, user, navigate]);
  
  // No more auto-migration - removing this effect
  
  // Set a max loading time - if the dashboard is still loading after this time,
  // we'll display action buttons for the user
  useEffect(() => {
    if (isLoading && !loadTimeout) {
      const timer = setTimeout(() => {
        setLoadTimeout(true);
        console.log("Dashboard loading timeout reached - data might not be loading correctly");
      }, 15000);
      
      return () => clearTimeout(timer);
    }
  }, [isLoading, loadTimeout]);
  
  // Handle timeout callback
  const handleTimeout = useCallback(() => {
    setLoadTimeout(true);
    setLoadAttempts(prev => prev + 1);
    console.log("Dashboard loading timeout reached - data might not be loading correctly");
    
    toast({
      title: "Dashboard taking too long to load",
      description: "There might be an issue with data loading. You can try again or go to Products.",
      variant: "destructive",
      duration: 10000,
    });
    
    // Try to refresh data one more time if we haven't tried too many times
    if (loadAttempts < 2) {
      refreshData().catch(err => {
        console.error("Failed to refresh data after timeout:", err);
      });
    }
  }, [toast, refreshData, loadAttempts]);
  
  // Retry loading
  const handleRetry = useCallback(() => {
    setLoadTimeout(false);
    setLoadAttempts(prev => prev + 1);
    refreshData();
  }, [refreshData]);
  
  // Navigate to products
  const handleGoToProducts = useCallback(() => {
    navigate("/products");
  }, [navigate]);
  
  // If still in authentication loading state, show a simple loading indicator
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  // If not authenticated, don't render anything (the useEffect will handle redirect)
  if (!user) {
    return null;
  }
  
  // If data is loading, show the loading indicator with timeout handling
  if (isLoading || loadTimeout) {
    return (
      <LoadingIndicator 
        onTimeout={handleTimeout} 
        onRetry={handleRetry}
        onGoToProducts={handleGoToProducts}
        showActionButtons={loadTimeout}
        timeout={10000} // Reduce timeout for better UX
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />
      <DashboardContent
        products={products}
        lowStockProducts={lowStockProducts}
        dismissedAlerts={dismissedAlerts}
        showMigration={false} // Always pass false to hide migration
        isMigrating={isMigrating}
        onDismissAlert={handleDismissAlert}
        onAddStock={handleAddStock}
        onUsageRecorded={handleUsageRecorded}
        onMigrateData={handleMigrateData}
      />
    </div>
  );
};

export default Dashboard;
